import React, { createContext, FC, useCallback, useRef, useEffect } from 'react';
import { AssistantPageProps } from 'types';
import { PageTrackingLabel, StaticPageProps } from '@modules/utilities/pageBuild/types/PageProps';
import {
  getEngineApiTypeTrackingLabel,
  getTrackableUrlPath,
  PageTrackingItemData,
  trackablePathMatch,
  getPageTrackingItemData,
} from '@modules/eventStream/utils/pageTrackingUtils';
import { engineApiTypeToTranslKey } from '@modules/apiReference/page/constants/apiReferenceConstants';
import creatorHistoryClient from '../../clients/creatorHistoryClient';
import unifiedLoggerClient from '../implementations/unifiedLoggerClient';

export type PageTrackingContext = {
  addPageTrackingDataByPath: (
    path: string,
    options: {
      label: PageTrackingLabel;
      title: string;
      description?: string;
      isTitleCode?: boolean;
    },
  ) => void;
  logNavigation: (urlOrPath: string) => void;
  trackRecentlyVisited: (urlOrPath: string) => Promise<void>;
};

export const PageTrackingContext = createContext<PageTrackingContext>({
  addPageTrackingDataByPath: () => {
    throw new Error('addPageTrackingDataByPath is not implemented');
  },
  logNavigation: () => {
    throw new Error('logNavigation is not implemented');
  },
  trackRecentlyVisited: async () => {
    throw new Error('trackRecentlyVisited is not implemented');
  },
});

type PageTrackingProviderProps = {
  pageProps: StaticPageProps | AssistantPageProps;
};

export type EngineApiPageEntityTrackingOptions = {
  isTitleCode?: boolean;
} & {
  type: string;
  name: string;
};

const getTypeForEngineApiPage = (path: string): keyof typeof engineApiTypeToTranslKey => {
  if (path.includes('/reference/engine/classes/')) {
    return 'Class';
  }
  if (path.includes('/reference/engine/datatypes/')) {
    return 'Datatype';
  }
  if (path.includes('/reference/engine/enums/')) {
    return 'Enum';
  }
  if (path.includes('/reference/engine/globals/')) {
    return 'GLOBAL';
  }
  if (path.includes('/reference/engine/libraries/')) {
    return 'LibraryReference';
  }
  return '';
};

const PageTrackingProvider: FC<React.PropsWithChildren<PageTrackingProviderProps>> = ({
  pageProps,
  children,
}) => {
  const pageTrackingDataByHash = useRef<Record<string, Partial<PageTrackingItemData>>>({});

  const addPageTrackingDataByPath = useCallback(
    (
      path: string,
      options: {
        label?: PageTrackingLabel;
        title: string;
        description?: string;
        isTitleCode?: boolean;
      },
    ) => {
      if (pageTrackingDataByHash.current) {
        pageTrackingDataByHash.current[trackablePathMatch(getTrackableUrlPath(path))] = {
          ...options,
        };
      }
    },
    [],
  );
  const addEngineApiEntityTrackingData = useCallback(
    (
      path: string, // note: can be empty string for default path label
      options: EngineApiPageEntityTrackingOptions,
    ) => {
      const { type = getTypeForEngineApiPage(path), name = '' } = options;
      if (!type || !name)
        throw new Error(`type and name are required. Got options: ${JSON.stringify(options)}`);
      const label = getEngineApiTypeTrackingLabel(type);
      addPageTrackingDataByPath(path, {
        label,
        title: name,
        isTitleCode: options.isTitleCode ?? true,
      });
    },
    [addPageTrackingDataByPath],
  );

  const trackRecentlyVisited = useCallback(
    async (urlOrPath: string) => {
      if (urlOrPath?.includes('/reference/engine/')) {
        const pageTitle = 'data' in pageProps ? pageProps.data.title || '' : '';
        addEngineApiEntityTrackingData(urlOrPath, {
          type: getTypeForEngineApiPage(urlOrPath),
          name: document.title.split(' | ')[0] || pageTitle,
          isTitleCode: true,
        });
      }
      if (!pageTrackingDataByHash || !pageTrackingDataByHash.current) {
        return;
      }
      const trackablePath = getTrackableUrlPath(urlOrPath);

      // Paths to skip from tracking (system pages, API routes)
      const SKIP_PATHS = ['/', '/api/', '/_next/', '/404', '/500'];
      if (SKIP_PATHS.some((skip) => trackablePath === skip)) {
        return;
      }
      try {
        const pageTrackingData = getPageTrackingItemData(
          pageProps,
          trackablePath,
          pageTrackingDataByHash.current,
        );
        if (pageTrackingData === null) {
          return;
        }
        await creatorHistoryClient.addToRecentlyVisited({
          id: trackablePath,
          path: trackablePath,
          accessedAt: new Date().getTime(),
          ...pageTrackingData,
        });
      } catch (error) {
        if (process.env.environment !== 'production')
          // eslint-disable-next-line no-console
          console.error('Failed to track recently visited:', error);
      }
    },
    [pageProps, addEngineApiEntityTrackingData],
  );

  const logNavigation = useCallback(
    async (urlOrPath: string) => {
      await trackRecentlyVisited(urlOrPath);
    },
    [trackRecentlyVisited],
  );

  // Auto-track page loads
  useEffect(() => {
    if (typeof window === 'undefined') return;
    logNavigation(window.location.href);
  }, [logNavigation]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    unifiedLoggerClient.trackPageLoad();
  }, []);

  return (
    <PageTrackingContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        addPageTrackingDataByPath,
        logNavigation,
        trackRecentlyVisited,
      }}>
      {children}
    </PageTrackingContext.Provider>
  );
};

export default PageTrackingProvider;
